import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiBookOpen, FiClipboard, FiCalendar } = FiIcons;

const StudentReviewModal = ({ student, session, onClose }) => {
  const { addStudentReview } = useData();
  const { user } = useAuth();
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      return alert('Please provide written feedback for the student.');
    }

    setSubmitting(true);
    try {
      const tutorName = user.name || `${user.firstName} ${user.lastName}`.trim();
      
      await addStudentReview({
        tutorId: user.id || user.uid,
        tutorName: tutorName,
        studentId: student.id,
        studentName: student.studentName,
        sessionId: session?.id, // Explicitly linked to unique session
        sessionDate: session?.date, // Captured for parent visibility
        subject: session?.subject, // Captured for parent visibility
        parentId: student.parentId,
        comment: comment.trim(),
        createdAt: new Date().toISOString()
      });
      
      alert('Session report submitted successfully!');
      onClose();
    } catch (error) {
      alert('Failed to submit report.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60] backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        className="bg-white rounded-[2.5rem] shadow-2xl max-w-md w-full overflow-hidden"
      >
        <div className="flex items-center justify-between p-8 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600">
              <SafeIcon icon={FiClipboard} className="text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900 tracking-tight">Tutor's Report</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Academic Feedback</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 rounded-xl transition-all">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Session Context Header */}
          <div className="bg-gray-900 rounded-2xl p-5 text-white space-y-3 shadow-lg shadow-indigo-100">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-black uppercase tracking-widest text-indigo-400">Class Focus</span>
              <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-indigo-400">
                <SafeIcon icon={FiCalendar} />
                {new Date(session.date).toLocaleDateString(undefined, { dateStyle: 'medium' })}
              </div>
            </div>
            <div className="flex justify-between items-end">
              <p className="text-xl font-black">{session.subject}</p>
              <p className="text-xs font-bold text-gray-400">{student.studentName}</p>
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">
              Performance & Progress Notes
            </label>
            <textarea 
              value={comment} 
              onChange={(e) => setComment(e.target.value)} 
              rows={5} 
              className="w-full p-5 bg-gray-50 border-2 border-transparent rounded-[1.5rem] focus:bg-white focus:border-indigo-600 outline-none font-bold text-gray-700 transition-all" 
              placeholder="What did the student learn? Any specific areas for improvement?" 
              required 
            />
          </div>

          <div className="flex flex-col gap-3">
            <button 
              type="submit" 
              disabled={submitting || !comment.trim()} 
              className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Session Report'}
            </button>
            <button type="button" onClick={onClose} className="w-full py-2 text-gray-400 font-bold text-xs uppercase tracking-widest hover:text-gray-600">
              Discard
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default StudentReviewModal;